import type { Route, TmNamedRouteRecord } from '@/services/route/types'
import PassThrough from '@/components/layout/PassThrough.vue'
import { AppModule } from '@/config/types'
import type { CallForwardingTitlerServiceParams } from '@/services/domain/senderSettings/callForwardingTitlerService'
import type {
  NotFoundResolverParams,
  ProgressResolverParams,
  RedirectableSingleResolverParams,
  SingleModelResolverParams,
} from '@/services/resolvers/types'
import PhoneNumber from '@/data/models/domain/PhoneNumber'
import type { ComponentProps } from '@/composition/types'
import type SettingsSmsVoiceByocCallForwarding from '@/components/views/settings/SettingsSmsVoiceByocCallForwarding.vue'
import type SettingsSmsVoiceByocCarrierDetails from '@/components/views/settings/SettingsSmsVoiceByocCarrierDetails.vue'
import TenDlcCampaign from '@/data/models/domain/services/regulations/TenDlcCampaign'
import type SettingsSmsVoiceTenDlcCampaignDetails from '@/components/views/settings/SettingsSmsVoiceTenDlcCampaignDetails.vue'
import type SettingsSmsVoiceTenDlcCampaignDetailsOverview from '@/components/views/settings/SettingsSmsVoiceTenDlcCampaignDetailsOverview.vue'
import type SettingsSmsVoiceTenDlcCampaignDetailsNumbers from '@/components/views/settings/SettingsSmsVoiceTenDlcCampaignDetailsNumbers.vue'
import type { TmRoutesDictionary } from '@/services/route/routerTypes'
import type SettingsSmsVoiceBuyNumberStep1 from '@/components/views/settings/SettingsSmsVoiceBuyNumberStep1.vue'
import type SettingsSmsVoiceBuyNumberCallForwarding from '@/components/views/settings/SettingsSmsVoiceBuyNumberCallForwarding.vue'
import TicketInbox from '@/data/models/domain/tickets/TicketInbox'
import type SettingsEmailFlow from '@/components/views/settings/SettingsEmailFlow.vue'
import InstagramAccount from '@/data/models/domain/messenger/instagram/InstagramAccount'
import WebWidgetDetails from '@/data/models/domain/messages/settings/webWidget/webWidgetDetails'
import FacebookPage from '@/data/models/domain/messenger/facebook/FacebookPage'
import type SettingsLiveChatEdit from '@/components/views/settings/SettingsLiveChatEdit.vue'
import type SettingsWhatsAppFlow from '@/components/views/settings/SettingsWhatsAppFlow.vue'
import type SettingsWhatsAppAccountDetails from '@/components/views/settings/SettingsWhatsAppAccountDetails.vue'
import type SettingsWhatsAppPhoneDetails from '@/components/views/settings/SettingsWhatsAppPhoneDetails.vue'
import FacebookConfig from '@/data/models/domain/messenger/facebook/FacebookConfig'
import type SettingsFacebookFlow from '@/components/views/settings/SettingsFacebookFlow.vue'
import type SettingsFacebookDetails from '@/components/views/settings/SettingsFacebookDetails.vue'
import type SettingsInstagramFlow from '@/components/views/settings/SettingsInstagramFlow.vue'
import type SettingsInstagramDetails from '@/components/views/settings/SettingsInstagramDetails.vue'
import Contact from '@/data/models/domain/Contact'
import { CustomFieldSchema } from '@/data/models/domain/CustomFieldSchema'
import ApiV1Settings from '@/data/models/domain/service/api/ApiV1Settings'
import ApiV2Settings from '@/data/models/domain/service/api/ApiV2Settings'
import DistributionDetails from '@/data/models/domain/services/distribution/DistributionDetails'
import type SettingsEmailToSmsDistributionListFlow from '@/components/views/settings/SettingsEmailToSmsDistributionListFlow.vue'
import AutomationRuleDetails from '@/data/models/domain/services/automationRule/AutomationRuleDetails'
import AutomationRuleLog from '@/data/models/domain/services/automationRule/AutomationRuleLog'
import type SettingsAutomationRulesFlow from '@/components/views/settings/SettingsAutomationRulesFlow.vue'
import type SettingsAutomationRuleLogDetails from '@/components/views/settings/SettingsAutomationRuleLogDetails.vue'
import AccountSettingsSecurity from '@/data/models/domain/AccountSettingsSecurity'
import TaskBoard from '@/data/models/domain/tasks/TaskBoard'
import TaskBoardArchived from '@/data/models/domain/tasks/TaskBoardArchived'
import DealPipeline from '@/data/models/domain/deals/DealPipeline'
import DealPipelineArchived from '@/data/models/domain/deals/DealPipelineArchived'
import AccountSsoSettings from '@/data/models/domain/account/AccountSsoSettings'
import TicketSpamFilter, { TICKET_SPAM_FILTER_ID } from '@/data/models/domain/tickets/TicketSpamFilter'
import TicketWorkspace, { TICKET_WORKSPACE_ID } from '@/data/models/domain/tickets/TicketWorkspace'
import TicketStatus from '@/data/models/domain/tickets/TicketStatus'
import { FilteredViewsSupportedEntities } from '@/data/models/domain/filteredViews/types'
import type { EditableFilteredViewsResolverServiceParams } from '@/services/domain/filteredViews/resolvers/types'
import type SettingsTicketsFilteredViewsDetails from '@/components/views/settings/SettingsTicketsFilteredViewsDetails.vue'
import User from '@/data/models/domain/User'
import type { DomainTitlerParams } from '@/services/route/titlers/types'
import SubAccountsImportDetails from '@/data/models/domain/subAccounts/import/SubAccountsImportDetails'
import type SettingsWorkspaceSubaccountsImportDetails from '@/components/views/settings/SettingsWorkspaceSubaccountsImportDetails.vue'
import { FILTERED_VIEW_PARAM_NAME } from '@/services/domain/filteredViews/const'
import { ExternalIntegration } from '@/data/models/domain/externalIntegrations'
import ConnectedApp from '@/data/models/domain/consent/ConnectedApp'
import { CONNECTED_APP_ID_PARAM_NAME } from '@/services/domain/consent/types'
import type { ExternalIntegrationTitlerServiceParams } from '@/services/domain/externalIntegrations/externalIntegrationTitlerService'
import type { ExternalIntegrationSetupTitlerServiceParams } from '@/services/domain/externalIntegrations/externalIntegrationSetupTitlerService'

export enum UserSettingsRoutes {
  Index = 'user.settings',
  SmsVoice = 'user.settings.smsVoice',
  SmsVoiceIndex = 'user.settings.smsVoice.index',
  SmsVoiceNumbers = 'user.settings.smsVoice.numbers',
  SmsVoiceBuyNumberIndex = 'user.settings.smsVoice.buyNumberIndex',
  SmsVoiceBuyNumber = 'user.settings.smsVoice.buyNumber',
  SmsVoiceSenderIds = 'user.settings.smsVoice.senderIds',
  SmsVoiceCallForwarding = 'user.settings.smsVoice.callForwarding',
  SmsVoiceByocIndex = 'user.settings.smsVoice.byocIndex',
  SmsVoiceByocBase = 'user.settings.smsVoice.byoc.base',
  SmsVoiceByocEdit = 'user.settings.smsVoice.byoc.edit',
  SmsVoiceByocCallForwarding = 'user.settings.smsVoice.byoc.callForwarding',
  SmsVoiceByocFlow = 'user.settings.smsVoice.byoc.flow',
  SmsVoiceByocProviders = 'user.settings.smsVoice.byoc.providers',
  SmsVoiceByocPhones = 'user.settings.smsVoice.byoc.phones',
  SmsVoiceByocSenderIds = 'user.settings.smsVoice.byoc.senderIds',
  SmsVoiceSenderSettings = 'user.settings.smsVoice.senderSettings',
  SmsVoiceTenDlcIndex = 'user.settings.smsVoice.tenDlcIndex',
  SmsVoiceTenDlc = 'user.settings.smsVoice.tenDlc',
  SmsVoiceTenDlcCampaign = 'user.settings.smsVoice.tenDlc.campaign',
  SmsVoiceTenDlcCampaignOverview = 'user.settings.smsVoice.tenDlc.overview',
  SmsVoiceTenDlcCampaignNumbers = 'user.settings.smsVoice.tenDlc.numbers',
  SmsVoiceTollFree = 'user.settings.smsVoice.tollFree',
  EmailIndex = 'user.settings.emailIndex',
  Email = 'user.settings.email',
  EmailDomains = 'user.settings.email.domains',
  EmailFlow = 'user.settings.email.flow',
  LiveChatIndex = 'user.settings.liveChatIndex',
  LiveChat = 'user.settings.liveChat',
  LiveChatEdit = 'user.settings.liveChat.edit',
  WhatsAppIndex = 'user.settings.whatsAppIndex',
  WhatsApp = 'user.settings.whatsApp',
  WhatsAppFlow = 'user.settings.whatsApp.flow',
  WhatsAppAccountIndex = 'user.settings.whatsApp.accountIndex',
  WhatsAppAccount = 'user.settings.whatsApp.account',
  WhatsAppPhone = 'user.settings.whatsApp.phone',
  FacebookIndex = 'user.settings.facebookIndex',
  Facebook = 'user.settings.facebook',
  FacebookFlow = 'user.settings.facebook.flow',
  FacebookDetails = 'user.settings.facebook.details',
  InstagramIndex = 'user.settings.instagramIndex',
  Instagram = 'user.settings.instagram',
  InstagramFlow = 'user.settings.instagram.flow',
  InstagramDetails = 'user.settings.instagram.details',
  Messenger = 'user.settings.messenger',
  ContactsIndex = 'user.settings.contactsIndex',
  ContactsAll = 'user.settings.contacts.all',
  ContactsUnsubscribeRules = 'user.settings.contacts.unsubscribeRules',
  ContactsSharing = 'user.settings.contacts.sharing',
  ContactsCustomFields = 'user.settings.contacts.customFields',
  ApiIndex = 'user.settings.apiIndex',
  ApiV2Keys = 'user.settings.api.v2Keys',
  Apiv2Settings = 'user.settings.api.v2Settings',
  ApiV1 = 'user.settings.api.v1',
  IntegrationsIndex = 'user.settings.integrations.root',
  Integrations = 'user.settings.integrations',
  IntegrationsMcp = 'user.settings.integrations.mcp',
  IntegrationsMcpDetails = 'user.settings.integrations.mcp.details',
  IntegrationSetup = 'user.settings.integrations.setup',
  IntegrationDetails = 'user.settings.integrations.details',
  IntegrationSyncDetails = 'user.settings.integrations.syncDetails',
  EmailToSmsIndex = 'user.settings.emailToSmsIndex',
  EmailToSmsTabsIndex = 'user.settings.emailToSmsTabsIndex',
  EmailToSmsAllowedEmails = 'user.settings.emailToSms.allowedEmails',
  EmailToSmsSendReceiveSettings = 'user.settings.emailToSms.sendReceiveSettings',
  EmailToSmsDistributionLists = 'user.settings.emailToSms.distributionLists',
  EmailToSmsCreateDistribution = 'user.settings.emailToSms.createDistribution',
  EmailToSmsEditDistribution = 'user.settings.emailToSms.editDistribution',
  AutoRespondersIndex = 'user.settings.autoRespondersIndex',
  AutoRespondersTabsIndex = 'user.settings.autoRespondersTabsIndex',
  AutoResponders = 'user.settings.autoResponders',
  AutoRespondersKeywords = 'user.settings.autoResponders.keywords',
  AutoRespondersLogs = 'user.settings.autoResponders.logs',
  AutoRespondersNewRule = 'user.settings.autoResponders.newRule',
  AutoRespondersEditRule = 'user.settings.autoResponders.editRule',
  AutoRespondersLogsDetails = 'user.settings.autoResponders.logsDetails',
  AutoRespondersDetails = 'user.settings.autoResponders.details',
  TicketsIndex = 'user.settings.ticketsIndex',
  Tickets = 'user.settings.tickets',
  TicketsInboxesIndex = 'user.settings.tickets.inboxesIndex',
  TicketsInboxes = 'user.settings.tickets.inboxes',
  TicketsInboxesConnect = 'user.settings.tickets.inboxes.connect',
  TicketsInboxesDetails = 'user.settings.tickets.inboxes.details',
  TicketsSpamFilter = 'user.settings.tickets.spamFilter',
  TicketsGeneral = 'user.settings.tickets.general',
  TicketsFilteredViewsIndex = 'user.settings.tickets.filteredViewsIndex',
  TicketsFilteredViews = 'user.settings.tickets.filteredViews',
  TicketsFilteredViewsCreate = 'user.settings.tickets.filteredViews.create',
  TicketsFilteredViewsDetails = 'user.settings.tickets.filteredViews.details',
  Tasks = 'user.settings.tasks',
  TasksOverview = 'user.settings.tasks.overview',
  TasksBoardRoot = 'user.settings.tasks.board.root',
  TasksBoard = 'user.settings.tasks.board',
  TasksBoardArchived = 'user.settings.tasks.archivedBoard',
  TasksBoardStage = 'user.settings.tasks.board.stage',
  Deals = 'user.settings.deals',
  DealsOverview = 'user.settings.deals.overview',
  DealsPipelineRoot = 'user.settings.deals.pipeline.root',
  DealsPipeline = 'user.settings.deals.pipeline',
  DealsPipelineArchived = 'user.settings.deals.archivedPipeline',
  DealsPipelineStage = 'user.settings.deals.pipeline.stage',
  PointAiIndex = 'user.settings.pointAiIndex',
  PointAi = 'user.settings.pointAi',
  PointAiWebsites = 'user.settings.pointAiWebsites',
  PointAiDocuments = 'user.settings.pointAiDocuments',
  PointAiWebsitesDownloaded = 'user.settings.pointAiWebsitesDownloaded',
  WorkspaceIndex = 'user.settings.workspaceIndex',
  WorkspaceUsers = 'user.settings.workspace.users',
  WorkspaceSubaccounts = 'user.settings.workspace.subaccounts',
  WorkspaceSubaccountsImport = 'user.settings.workspace.subaccounts.import',
  WorkspaceSubaccountsImportDetailsIndex = 'user.settings.workspace.subaccounts.importDetailsIndex',
  WorkspaceSubaccountsImportDetailsInvited = 'user.settings.workspace.subaccounts.importDetails.invited',
  WorkspaceSubaccountsImportDetailsDuplicated = 'user.settings.workspace.subaccounts.importDetails.duplicated',
  WorkspaceSubaccountsImportDetailsFailed = 'user.settings.workspace.subaccounts.importDetails.failed',
  WorkspaceSubaccountsDetails = 'user.settings.workspace.subaccounts.details',
  WorkspaceSubaccountsDetailsOverviewPermissions = 'user.settings.workspace.subaccounts.details.overviewPermissions',
  WorkspaceSubaccountsDetailsOverview = 'user.settings.workspace.subaccounts.details.overview',
  WorkspaceSubaccountsDetailsPermissions = 'user.settings.workspace.subaccounts.details.permissions',
  WorkspaceSubaccountsDetailsSent = 'user.settings.workspace.subaccounts.details.sent',
  WorkspaceSubaccountsDetailsReceived = 'user.settings.workspace.subaccounts.details.received',
  WorkspaceUsersDetails = 'user.settings.workspace.users.details',
  WorkspaceUsersDetailsOverview = 'user.settings.workspace.users.details.overview',
  WorkspaceUsersDetailsPermissions = 'user.settings.workspace.users.details.permissions',
  WorkspaceUsersDetailsSent = 'user.settings.workspace.users.details.sent',
  WorkspaceUsersDetailsReceived = 'user.settings.workspace.users.details.received',
  WorkspaceSso = 'user.settings.workspace.sso',
  WorkspaceDataRetention = 'user.settings.workspace.dataRetention',
  AccountIndex = 'user.settings.accountIndex',
  AccountTabsIndex = 'user.settings.accountTabsIndex',
  AccountDetails = 'user.settings.account.details',
  AccountSecurity = 'user.settings.account.security',
  AccountSecurity2faSetup = 'user.settings.account.security.2faSetup',
  AccountNotifications = 'user.settings.account.notifications',
  BillingIndex = 'user.settings.billingIndex',
  BillingOverview = 'user.settings.billing.overview',
  BillingPlans = 'user.settings.billing.plans',
  BillingInvoices = 'user.settings.billing.invoices',
  BillingStatements = 'user.settings.billing.statements',
}

const getAutomationRuleDetails = () => {
  return [
    AppModule.AutomationRulesDetails,
    AppModule.AutomationRulesLog,
    AppModule.AutomationRule,
    AppModule.AutomationRules,
    AppModule.Compose,
    AppModule.Contacts,
    AppModule.ContactList,
    AppModule.ContactListCounter,
  ]
}

const getSubAccountsDetailsRoute = (): TmNamedRouteRecord => {
  return {
    name: UserSettingsRoutes.WorkspaceSubaccountsDetails,
    path: 'workspace/subaccounts/:subAccountId',
    meta: {
      resolverComponent: () => import('@/components/resolvers/DetailsHeroPageResolver.vue'),
      detailsPage: {
        source: 'router',
        requestParam: 'subAccountId',
        model: User,
      },
      titler: {
        service: 'DomainTitlerService',
        params: {
          model: User,
          field: 'fullName',
          idParamName: 'subAccountId',
        } as DomainTitlerParams,
      },
      redirectToIfNotFound: { name: UserSettingsRoutes.WorkspaceUsers },
      modules: [AppModule.SubAccountsDetails, AppModule.SubAccountsForm, AppModule.AccountSettings],
      permission: 'subaccountSettings',
      accessControlGroups: ['SubAccountDetailsStrategy'],
    },
    component: () => import('@/components/views/accounts/subAccountDetails/SettingsSubAccountDetails.vue'),
    redirect: { name: UserSettingsRoutes.WorkspaceSubaccountsDetailsOverviewPermissions },
    children: [
      {
        name: UserSettingsRoutes.WorkspaceSubaccountsDetailsOverviewPermissions,
        path: '',
        meta: {
          modules: [AppModule.AccountSettings, AppModule.AuditLogs],
        },
        component: () =>
          import('@/components/domain/accounts/subAccounts/details/SubAccountDetailsLayoutWithProfileInfo.vue'),
        redirect: { name: UserSettingsRoutes.WorkspaceSubaccountsDetailsOverview },
        props: (route) => ({
          subAccountId: route.params.subAccountId,
        }),
        children: [
          {
            name: UserSettingsRoutes.WorkspaceSubaccountsDetailsOverview,
            path: '',
            meta: {
              modules: [AppModule.AccountSettings, AppModule.AuditLogs],
            },
            component: () => import('@/components/views/accounts/subAccountDetails/SubAccountDetailsOverview.vue'),
            props: (route) => ({
              subAccountId: route.params.subAccountId,
            }),
          },
          {
            name: UserSettingsRoutes.WorkspaceSubaccountsDetailsPermissions,
            path: 'permissions',
            meta: {
              modules: [AppModule.AccountSettings],
            },
            component: () => import('@/components/views/accounts/subAccountDetails/SubAccountDetailsPermissions.vue'),
            props: (route) => ({
              subAccountId: route.params.subAccountId,
            }),
          },
        ],
      },
      {
        name: UserSettingsRoutes.WorkspaceSubaccountsDetailsSent,
        path: 'sent',
        component: () => import('@/components/views/accounts/subAccountDetails/SubAccountDetailsSentSms.vue'),
        props: (route) => ({
          subAccountId: route.params.subAccountId,
        }),
        meta: {
          modules: [
            AppModule.SentSmsBase,
            AppModule.HistorySentSms,
            AppModule.SenderSettingsCountries,
            AppModule.Compose,
          ],
        },
      },
      {
        name: UserSettingsRoutes.WorkspaceSubaccountsDetailsReceived,
        path: 'received',
        component: () => import('@/components/views/accounts/subAccountDetails/SubAccountDetailsReceivedSms.vue'),
        props: (route) => ({
          subAccountId: route.params.subAccountId,
        }),
        meta: {
          modules: [AppModule.HistoryReceivedSms, AppModule.Compose],
        },
      },
    ],
  }
}

const getUsersDetailsRoute = (): TmNamedRouteRecord => ({
  name: UserSettingsRoutes.WorkspaceUsersDetails,
  path: 'workspace/users/:subAccountId',
  meta: {
    permission: 'subaccountSettings',
    resolverComponent: () => import('@/components/resolvers/DetailsHeroPageResolver.vue'),
    detailsPage: {
      source: 'router',
      requestParam: 'subAccountId',
      model: User,
    } as SingleModelResolverParams,
    titler: {
      service: 'DomainTitlerService',
      params: {
        model: User,
        field: 'fullName',
        idParamName: 'subAccountId',
      } as DomainTitlerParams,
    },
    redirectToIfNotFound: { name: UserSettingsRoutes.WorkspaceIndex },
    modules: [AppModule.SubAccountsDetails, AppModule.SubAccountsForm, AppModule.AccountSettings],
    accessControlGroups: ['UserWorkflowStrategy'],
  },
  component: () => import('@/components/views/accounts/userDetails/SettingsUserDetails.vue'),
  props: (route) => ({
    userId: route.params.subAccountId,
  }),
  redirect: { name: UserSettingsRoutes.WorkspaceUsersDetailsOverview },
  children: [
    {
      name: UserSettingsRoutes.WorkspaceUsersDetailsOverview,
      path: '',
      meta: {
        modules: [AppModule.AccountSettings, AppModule.AuditLogs],
      },
      component: () => import('@/components/views/accounts/userDetails/UserDetailsOverview.vue'),
      props: (route) => ({
        accountId: route.params.subAccountId,
      }),
    },
    {
      name: UserSettingsRoutes.WorkspaceUsersDetailsPermissions,
      path: 'permissions',
      meta: {
        modules: [AppModule.AccountSettings],
      },
      component: () => import('@/components/views/accounts/subAccountDetails/SubAccountDetailsPermissions.vue'),
      props: (route) => ({
        subAccountId: route.params.subAccountId,
        isUsersWorkflow: true,
      }),
    },
  ],
})

export const settingsRoute: TmNamedRouteRecord = {
  name: UserSettingsRoutes.Index,
  path: 'settings',
  meta: {
    tags: ['USER_TOP_MENU'],
    menu: () => import('@/components/domain/settings/SettingsMenu.vue'),
    resolvers: [
      {
        service: 'SettingsSectionRedirectResolver',
      },
    ],
  },
  component: () => import('@/components/views/settings/Settings.vue'),
  redirect: { name: UserSettingsRoutes.SmsVoice },
  children: [
    {
      name: UserSettingsRoutes.SmsVoice,
      path: 'sms-voice',
      component: PassThrough,
      children: [
        {
          name: UserSettingsRoutes.SmsVoiceIndex,
          path: '',
          meta: {
            tabTitle: 'pageTitle.user.settings.smsVoice',
          },
          component: () => import('@/components/views/settings/SettingsSmsVoice.vue'),
        },
        {
          name: UserSettingsRoutes.SmsVoiceNumbers,
          path: 'numbers',
          meta: {
            tabTitle: 'pageTabTitle.user.settings.numbers',
            modules: [
              AppModule.TenDlcTollFree, // TollFreePhoneNumbersService via useTollFreeCancelPhoneNumber → PhoneNumbersTableDropdown
            ],
          },
          component: () => import('@/components/views/settings/SettingsSmsVoiceNumbers.vue'),
        },
        {
          name: UserSettingsRoutes.SmsVoiceBuyNumberIndex,
          path: 'buy-number',
          component: PassThrough,
          meta: {
            modules: [
              AppModule.BuyNumber,
              AppModule.ByocHelper,
              AppModule.TenDlcTollFree,
              AppModule.TenDlcTollFreeRequest,
            ],
          },
          redirect: { name: UserSettingsRoutes.SmsVoiceBuyNumber },
          children: [
            {
              name: UserSettingsRoutes.SmsVoiceBuyNumber,
              path: 'number',
              props: (route) => {
                return {
                  initCountryCode: route.query.country,
                  initTypeTollFree: route.query.typeTollFree,
                } satisfies ComponentProps<typeof SettingsSmsVoiceBuyNumberStep1>
              },
              meta: {
                tabTitle: 'pageTabTitle.user.settings.buyNumber.number',
                permission: 'buyNumbersSenderIds',
              },
              component: () => import('@/components/views/settings/SettingsSmsVoiceBuyNumberStep1.vue'),
            },
            {
              name: UserSettingsRoutes.SmsVoiceCallForwarding,
              path: 'call-forwarding/:id',
              props: (route) => {
                return {
                  phoneNumberId: route.params.id,
                } satisfies ComponentProps<typeof SettingsSmsVoiceBuyNumberCallForwarding>
              },
              meta: {
                tabTitle: 'pageTabTitle.user.settings.buyNumber.callForwarding',
                titler: {
                  service: 'CallForwardingTitlerService',
                  params: {
                    phoneId: 'id',
                  } satisfies CallForwardingTitlerServiceParams<any>,
                },
                permission: 'senderSettings',
                resolvers: [
                  {
                    service: 'RedirectableSingleModelResolverService',
                    params: {
                      model: PhoneNumber,
                      source: 'router',
                      requestParam: 'id',
                      redirectToIfNotFound: { name: UserSettingsRoutes.SmsVoiceBuyNumberIndex },
                    } satisfies NotFoundResolverParams,
                  },
                ],
              },
              component: () => import('@/components/views/settings/SettingsSmsVoiceBuyNumberCallForwarding.vue'),
            },
          ],
        },
        {
          name: UserSettingsRoutes.SmsVoiceSenderIds,
          path: 'sender-ids',
          meta: {
            tabTitle: 'pageTabTitle.user.settings.senderIds',
            accessControlGroups: ['SenderIdsStrategy'],
          },
          component: () => import('@/components/views/settings/SettingsSmsVoiceSenderIds.vue'),
        },
        {
          name: UserSettingsRoutes.SmsVoiceByocIndex,
          path: 'byoc',
          redirect: { name: UserSettingsRoutes.SmsVoiceByocProviders },
          children: [
            {
              name: UserSettingsRoutes.SmsVoiceByocBase,
              path: '',
              meta: {
                modules: [AppModule.Byoc],
                resolvers: [{ service: 'ByocResolverService' }],
                resolverComponent: () => import('@/components/domain/settings/SettingsByocContentResolver.vue'),
                permission: 'manageCpaasProviders',
                accessControlGroups: ['ByocStrategy'],
              },
              component: () => import('@/components/views/settings/SettingsSmsVoiceByoc.vue'),
              redirect: { name: UserSettingsRoutes.SmsVoiceByocProviders },
              children: [
                {
                  name: UserSettingsRoutes.SmsVoiceByocProviders,
                  path: 'providers',
                  component: () => import('@/components/views/services/byoc/ByocProviders.vue'),
                  meta: {
                    tabTitle: 'pageTabTitle.user.settings.byoc.providers',
                  },
                },
                {
                  name: UserSettingsRoutes.SmsVoiceByocPhones,
                  path: 'numbers',
                  component: () => import('@/components/views/services/byoc/ByocPhones.vue'),
                  meta: {
                    tabTitle: 'pageTabTitle.user.settings.byoc.phones',
                    resolvers: [{ service: 'ByocPhonesResolver' }],
                  },
                },
                {
                  name: UserSettingsRoutes.SmsVoiceByocSenderIds,
                  path: 'sender-ids',
                  component: () => import('@/components/views/services/byoc/ByocSenderIds.vue'),
                  meta: {
                    tabTitle: 'pageTabTitle.user.settings.byoc.senderIds',
                    resolvers: [{ service: 'ByocSenderIdsResolver' }],
                  },
                },
              ],
            },
            {
              name: UserSettingsRoutes.SmsVoiceByocEdit,
              path: ':provider/:carrierId',
              props: (route: Route) => {
                return {
                  provider: route.params.provider,
                  carrierId: route.params.carrierId,
                } satisfies ComponentProps<typeof SettingsSmsVoiceByocCarrierDetails>
              },
              meta: {
                tabTitle: 'pageTabTitle.user.settings.byoc.details',
                resolvers: [{ service: 'ByocCarrierResolver' }],
                modules: [AppModule.Byoc, AppModule.SenderSettings],
                permission: 'manageCpaasProviders',
              },
              component: () => import('@/components/views/settings/SettingsSmsVoiceByocCarrierDetails.vue'),
            },
            {
              name: UserSettingsRoutes.SmsVoiceByocCallForwarding,
              path: ':provider/:carrierId/:phoneNumberId/call-forwarding',
              props: (route: Route) => {
                return {
                  provider: route.params.provider,
                  phoneNumberId: route.params.phoneNumberId,
                  carrierId: route.params.carrierId,
                } satisfies ComponentProps<typeof SettingsSmsVoiceByocCallForwarding>
              },
              meta: {
                tabTitle: 'pageTabTitle.user.settings.byoc.callForwarding',
                resolvers: [{ service: 'ByocCarrierResolver' }],
                modules: [AppModule.Byoc],
                permission: 'manageCpaasProviders',
              },
              component: () => import('@/components/views/settings/SettingsSmsVoiceByocCallForwarding.vue'),
            },
            {
              name: UserSettingsRoutes.SmsVoiceByocFlow,
              path: 'flow',
              component: () => import('@/components/views/settings/SettingsSmsVoiceByocFlow.vue'),
              meta: {
                tabTitle: 'pageTabTitle.user.settings.byoc.flow',
                permission: 'manageCpaasProviders',
                modules: [AppModule.Byoc],
              },
            },
          ],
        },
        {
          name: UserSettingsRoutes.SmsVoiceSenderSettings,
          path: 'sender-settings',
          component: () => import('@/components/views/settings/SettingsSmsVoiceSenderSettings.vue'),
          meta: {
            tabTitle: 'pageTabTitle.user.settings.senderSettings',
          },
        },
        {
          name: UserSettingsRoutes.SmsVoiceTenDlcIndex,
          path: '10dlc',
          component: PassThrough,
          meta: {
            tabTitle: 'pageTabTitle.user.settings.tenDlc',
            accessControlGroups: ['TenDlcStrategy'],
            modules: [AppModule.TenDlcTollFree],
          },
          redirect: { name: UserSettingsRoutes.SmsVoiceTenDlc },
          children: [
            {
              name: UserSettingsRoutes.SmsVoiceTenDlc,
              path: '',
              component: () => import('@/components/views/settings/SettingsSmsVoiceTenDlc.vue'),
              meta: {
                tabTitle: 'pageTabTitle.user.settings.tenDlc',
                accessControlGroups: ['TenDlcStrategy'],
                modules: [AppModule.TenDlcTollFree],
              },
            },
            {
              name: UserSettingsRoutes.SmsVoiceTenDlcCampaign,
              path: 'campaign/:campaignId',
              props: (route) => {
                return {
                  campaignId: route.params.campaignId,
                } satisfies ComponentProps<typeof SettingsSmsVoiceTenDlcCampaignDetails>
              },
              component: () => import('@/components/views/settings/SettingsSmsVoiceTenDlcCampaignDetails.vue'),
              redirect: { name: UserSettingsRoutes.SmsVoiceTenDlcCampaignOverview },
              meta: {
                tabTitle: 'pageTabTitle.user.settings.tenDlcCampaignDetails',
                resolvers: [
                  {
                    service: 'RedirectableSingleModelResolverService',
                    params: {
                      source: 'router',
                      requestParam:
                        'campaignId' satisfies keyof TmRoutesDictionary[UserSettingsRoutes.SmsVoiceTenDlcCampaign]['params'],
                      model: TenDlcCampaign,
                      redirectToIfNotFound: { name: UserSettingsRoutes.SmsVoiceTenDlc },
                      cache: false,
                    } satisfies NotFoundResolverParams,
                  },
                  {
                    service: 'TenDlcNumbersCountResolver',
                    params: {
                      source: 'router',
                      requestParam:
                        'campaignId' satisfies keyof TmRoutesDictionary[UserSettingsRoutes.SmsVoiceTenDlcCampaign]['params'],
                      model: TenDlcCampaign,
                    },
                  },
                ],
                resolverComponent: () => import('@/components/domain/settings/SettingsCampaignDetailsPageResolver.vue'),
                accessControlGroups: ['TenDlcStrategy'],
                modules: [AppModule.TenDlcTollFreeRequest],
              },
              children: [
                {
                  name: UserSettingsRoutes.SmsVoiceTenDlcCampaignOverview,
                  path: '',
                  props: (route) => {
                    return {
                      campaignId: route.params.campaignId,
                    } satisfies ComponentProps<typeof SettingsSmsVoiceTenDlcCampaignDetailsOverview>
                  },
                  component: () =>
                    import('@/components/views/settings/SettingsSmsVoiceTenDlcCampaignDetailsOverview.vue'),
                },
                {
                  name: UserSettingsRoutes.SmsVoiceTenDlcCampaignNumbers,
                  path: 'numbers',
                  props: (route) => {
                    return {
                      campaignId: route.params.campaignId,
                    } satisfies ComponentProps<typeof SettingsSmsVoiceTenDlcCampaignDetailsNumbers>
                  },
                  component: () =>
                    import('@/components/views/settings/SettingsSmsVoiceTenDlcCampaignDetailsNumbers.vue'),
                },
              ],
            },
          ],
        },
        {
          name: UserSettingsRoutes.SmsVoiceTollFree,
          path: 'toll-free-verification',
          component: () => import('@/components/views/settings/SettingsSmsVoiceTollFree.vue'),
          meta: {
            tabTitle: 'pageTabTitle.user.settings.tollFree',
            accessControlGroups: ['TollFreeStrategy'],
            modules: [AppModule.TenDlcTollFree, AppModule.SenderSettings],
          },
        },
      ],
      redirect: { name: UserSettingsRoutes.SmsVoiceIndex },
    },
    {
      name: UserSettingsRoutes.EmailIndex,
      path: 'email',
      redirect: (to) => ({ name: UserSettingsRoutes.Email, params: to.params, query: to.query, hash: to.hash }),
      meta: {
        tabTitle: 'pageTabTitle.user.settings.emailSettings',
        permission: 'connectEmailDomains',
        modules: [AppModule.CampaignsSenders, AppModule.TmCodemirrorEditor],
        resolvers: [
          { service: 'SettingsEmailCampaignDomainsRouteResolverService' },
          { service: 'EmailCampaignDomainWebSocketResolver' },
          {
            service: 'ModelResolver',
            params: [TicketInbox],
          },
        ],
        resolverComponent: () => import('@/components/domain/settings/SettingsDetailsWithBlocksPageResolver.vue'),
        resolverComponentProps: { hideBreadcrumbsSkeleton: true },
      },
      children: [
        {
          name: UserSettingsRoutes.Email,
          path: '',
          component: () => import('@/components/views/settings/SettingsEmail.vue'),
        },
        {
          name: UserSettingsRoutes.EmailDomains,
          path: 'domains',
          component: () => import('@/components/views/settings/SettingsEmailDomains.vue'),
          meta: {
            tabTitle: 'pageTitle.user.settings.domainsAndSenders',
            resolvers: [{ service: 'EmailCampaignDomainsRouteResolverService' }],
            resolverComponent: () => import('@/components/domain/settings/SettingsEmailDomainsDetailsPageResolver.vue'),
          },
        },
        {
          name: UserSettingsRoutes.EmailFlow,
          path: `flow/:domainId?`,
          props: (route) => {
            return {
              domainId: route.params.domainId,
            } satisfies ComponentProps<typeof SettingsEmailFlow>
          },
          component: () => import('@/components/views/settings/SettingsEmailFlow.vue'),
          meta: {
            tabTitle: 'pageTitle.user.settings.domainsAndSenders',
            resolvers: [{ service: 'SettingsEmailCampaignDomainResolverService' }],
            resolverComponent: () => import('@/components/domain/settings/SettingsFlowDetailsPageResolver.vue'),
          },
        },
      ],
    },
    {
      name: UserSettingsRoutes.LiveChatIndex,
      path: 'live-chat',
      meta: {
        modules: [AppModule.WebWidgets, AppModule.WhatsApp],
        accessControlGroups: ['WebWidgetsStrategy'],
        tabTitle: 'pageTitle.user.chatsSettings.webWidgets',
        permission: 'connectChatWidget',
      },
      component: PassThrough,
      redirect: { name: UserSettingsRoutes.LiveChat },
      children: [
        {
          name: UserSettingsRoutes.LiveChat,
          path: '',
          component: () => import('@/components/views/settings/SettingsLiveChat.vue'),
        },
        {
          name: UserSettingsRoutes.LiveChatEdit,
          path: ':id',
          props: (route) => {
            return {
              id: route.params.id,
            } satisfies ComponentProps<typeof SettingsLiveChatEdit>
          },
          component: () => import('@/components/views/settings/SettingsLiveChatEdit.vue'),
          meta: {
            resolverComponent: () => import('@/components/domain/settings/SettingsFlowDetailsPageResolver.vue'),
            resolvers: [
              {
                service: 'ModelResolver',
                params: [TicketInbox, FacebookPage, InstagramAccount, CustomFieldSchema],
              },
              {
                service: 'RedirectableSingleModelResolverService',
                params: {
                  model: WebWidgetDetails,
                  source: 'router',
                  requestParam: 'id',
                  redirectToIfNotFound: { name: UserSettingsRoutes.LiveChatIndex },
                  redirectToIfAccessError: { name: UserSettingsRoutes.LiveChatIndex },
                } as RedirectableSingleResolverParams,
              },
            ],
          },
        },
      ],
    },
    {
      name: UserSettingsRoutes.WhatsAppIndex,
      path: 'whatsapp',
      meta: {
        tabTitle: 'pageTitle.user.settings.whatsApp',
        permission: 'connectWhatsapp',
        modules: [AppModule.WhatsApp],
        resolvers: [
          {
            service: 'WhatsAppResolver',
          },
        ],
        resolverComponent: () => import('@/components/domain/settings/SettingsDetailsPageResolver.vue'),
        isNew: true,
      },
      component: PassThrough,
      redirect: { name: UserSettingsRoutes.WhatsApp },
      children: [
        {
          name: UserSettingsRoutes.WhatsApp,
          path: '',
          component: () => import('@/components/views/settings/SettingsWhatsApp.vue'),
        },
        {
          name: UserSettingsRoutes.WhatsAppFlow,
          path: 'flow/:stepNumber?',
          props: (route) => {
            return {
              stepNumber: route.params.stepNumber,
            } satisfies ComponentProps<typeof SettingsWhatsAppFlow>
          },
          meta: {
            tabTitle: 'pageTabTitle.user.settings.connectWhatsApp',
          },
          component: () => import('@/components/views/settings/SettingsWhatsAppFlow.vue'),
        },
        {
          name: UserSettingsRoutes.WhatsAppAccountIndex,
          path: 'account/:id',
          component: PassThrough,
          redirect: { name: UserSettingsRoutes.WhatsAppAccount },
          children: [
            {
              name: UserSettingsRoutes.WhatsAppAccount,
              path: '',
              props: (route) => {
                return {
                  id: route.params.id,
                } satisfies ComponentProps<typeof SettingsWhatsAppAccountDetails>
              },
              meta: {
                tabTitle: 'pageTabTitle.user.settings.whatsAppAccount',
              },
              component: () => import('@/components/views/settings/SettingsWhatsAppAccountDetails.vue'),
            },
            {
              name: UserSettingsRoutes.WhatsAppPhone,
              path: 'phone/:phoneId',
              props: (route) => {
                return {
                  id: route.params.id,
                  phoneId: route.params.phoneId,
                } satisfies ComponentProps<typeof SettingsWhatsAppPhoneDetails>
              },
              meta: {
                tabTitle: 'pageTabTitle.user.settings.whatsAppNumber',
              },
              component: () => import('@/components/views/settings/SettingsWhatsAppPhoneDetails.vue'),
            },
          ],
        },
      ],
    },
    {
      name: UserSettingsRoutes.FacebookIndex,
      path: 'facebook',
      redirect: { name: UserSettingsRoutes.Facebook },
      meta: {
        modules: [AppModule.Facebook],
        tabTitle: 'pageTitle.user.settings.facebook',
        permission: 'connectFacebook',
        resolvers: [
          {
            service: 'ChatFacebookResolver',
          },
        ],
      },
      children: [
        {
          name: UserSettingsRoutes.Facebook,
          path: '',
          component: () => import('@/components/views/settings/SettingsFacebook.vue'),
        },
        {
          name: UserSettingsRoutes.FacebookFlow,
          path: 'flow',
          props: (route) => {
            return {
              token: route.query.token,
            } satisfies ComponentProps<typeof SettingsFacebookFlow>
          },
          component: () => import('@/components/views/settings/SettingsFacebookFlow.vue'),
          meta: {
            resolvers: [
              {
                service: 'SingleSettingsModelResolverService',
                params: { model: FacebookConfig },
              },
            ],
          },
        },
        {
          name: UserSettingsRoutes.FacebookDetails,
          path: ':id',
          props: (route) => {
            return {
              id: route.params.id,
            } satisfies ComponentProps<typeof SettingsFacebookDetails>
          },
          component: () => import('@/components/views/settings/SettingsFacebookDetails.vue'),
        },
      ],
    },
    {
      name: UserSettingsRoutes.InstagramIndex,
      path: 'instagram',
      redirect: { name: UserSettingsRoutes.Instagram },
      meta: {
        modules: [AppModule.Instagram],
        tabTitle: 'pageTitle.user.settings.instagram',
        permission: 'connectInstagram',
        resolvers: [
          {
            service: 'ChatInstagramResolver',
          },
        ],
      },
      children: [
        {
          name: UserSettingsRoutes.Instagram,
          path: '',
          component: () => import('@/components/views/settings/SettingsInstagram.vue'),
        },
        {
          name: UserSettingsRoutes.InstagramFlow,
          path: 'flow',
          component: () => import('@/components/views/settings/SettingsInstagramFlow.vue'),
          props: (route) => {
            return {
              code: route.query.code,
            } satisfies ComponentProps<typeof SettingsInstagramFlow>
          },
        },
        {
          name: UserSettingsRoutes.InstagramDetails,
          path: ':id',
          props: (route) => {
            return {
              id: route.params.id,
            } satisfies ComponentProps<typeof SettingsInstagramDetails>
          },
          component: () => import('@/components/views/settings/SettingsInstagramDetails.vue'),
        },
      ],
    },
    {
      name: UserSettingsRoutes.Messenger,
      path: 'messenger',
      meta: {
        tabTitle: 'pageTabTitle.user.settings.messenger',
        modules: [AppModule.Chat],
      },
      component: () => import('@/components/views/settings/SettingsMessenger.vue'),
    },
    {
      name: UserSettingsRoutes.ContactsIndex,
      path: 'contacts',
      meta: {
        tabTitle: 'pageTabTitle.user.settings.contacts',
        permission: 'contacts',
        modules: [
          AppModule.Contacts,
          AppModule.ContactList,
          AppModule.ContactCounter,
          AppModule.ContactsUnsubscribed,
          AppModule.ContactsBlocked,
          AppModule.PinnedContacts,
        ],
      },
      redirect: { name: UserSettingsRoutes.ContactsAll },
      children: [
        {
          path: 'all',
          name: UserSettingsRoutes.ContactsAll,
          meta: {
            tabTitle: 'pageTabTitle.user.settings.contacts',
          },
          component: () => import('@/components/views/settings/SettingsContacts.vue'),
        },
        {
          path: 'unsubscribe-rules',
          name: UserSettingsRoutes.ContactsUnsubscribeRules,
          component: () => import('@/components/views/settings/SettingsContactsUnsubscribeRules.vue'),
          meta: {
            tabTitle: 'pageTitle.user.settings.unsubscribeRules',
            modules: [AppModule.Templates],
          },
        },
        {
          path: 'sharing',
          name: UserSettingsRoutes.ContactsSharing,
          component: () => import('@/components/views/settings/SettingsContactsSharing.vue'),
          meta: {
            tabTitle: 'pageTitle.user.settings.sharing',
            accessControlGroups: ['SubAccountsWorkflowStrategy'],
          },
        },
        {
          path: 'custom-fields',
          name: UserSettingsRoutes.ContactsCustomFields,
          meta: {
            tabTitle: 'pageTitle.user.settings.customFields',
            resolvers: [
              {
                service: 'FacetResolver',
                params: {
                  model: Contact,
                  subscriptionModels: [CustomFieldSchema],
                },
              },
            ],
          },
          component: () => import('@/components/views/settings/SettingsContactsCustomFields.vue'),
        },
      ],
    },
    {
      name: UserSettingsRoutes.ApiIndex,
      path: 'api',
      meta: {
        permission: 'api',
        modules: [AppModule.ServicesApi],
      },
      component: () => import('@/components/views/settings/SettingsApi.vue'),
      redirect: { name: UserSettingsRoutes.ApiV2Keys },
      children: [
        {
          name: UserSettingsRoutes.ApiV2Keys,
          path: '',
          component: () => import('@/components/views/services/api/ApiV2Keys.vue'),
          meta: {
            tabTitle: 'pageTitle.user.settings.apiv2Keys',
          },
        },
        {
          name: UserSettingsRoutes.Apiv2Settings,
          path: 'api-v2-settings',
          component: () => import('@/components/views/services/api/ApiV2Settings.vue'),
          meta: {
            tabTitle: 'pageTitle.user.settings.apiv2Settings',
            resolvers: [
              {
                service: 'ModelSettingsResolverService',
                params: {
                  model: ApiV2Settings,
                  cache: false,
                },
              },
            ],
          },
        },
        {
          name: UserSettingsRoutes.ApiV1,
          path: 'api-v1',
          meta: {
            tabTitle: 'pageTitle.user.settings.apiv1',
            resolvers: [
              {
                service: 'ModelSettingsResolverService',
                params: {
                  model: ApiV1Settings,
                  cache: false,
                },
              },
            ],
          },
          component: () => import('@/components/views/services/api/ApiV1Settings.vue'),
        },
      ],
    },
    {
      name: UserSettingsRoutes.IntegrationsIndex,
      path: 'integrations',
      meta: {
        permission: 'api',
        tabTitle: 'pageTitle.user.settings.integrations',
        modules: [AppModule.ExternalIntegrations],
      },
      redirect: { name: UserSettingsRoutes.Integrations },
      children: [
        {
          name: UserSettingsRoutes.Integrations,
          path: 'apps',
          component: () => import('@/components/views/settings/SettingsIntegrationsApps.vue'),
          meta: {
            tabTitle: 'pageTitle.user.settings.integrations',
            resolvers: [
              {
                service: 'ShopifyAutoconnectRedirectResolver',
              },
              {
                service: 'ExternalIntegrationStatusResolver',
              },
            ],
          },
        },
        {
          name: UserSettingsRoutes.IntegrationsMcp,
          path: 'mcp',
          component: () => import('@/components/views/settings/SettingsIntegrationsMcp.vue'),
          meta: {
            tabTitle: 'pageTitle.user.settings.integrationsMcp',
            modules: [AppModule.Consent],
          },
        },
        {
          name: UserSettingsRoutes.IntegrationSetup,
          path: ':provider/setup',
          component: () => import('@/components/views/settings/ExternalIntegrationSetup.vue'),
          props: (route) => ({
            provider: route.params.provider,
          }),
          meta: {
            titler: {
              service: 'ExternalIntegrationSetupTitlerService',
              params: {
                providerKey: 'provider',
              } satisfies ExternalIntegrationSetupTitlerServiceParams,
            },
            resolvers: [
              {
                service: 'ExternalIntegrationsResolver',
              },
              {
                service: 'ExternalIntegrationStatusResolver',
              },
              {
                service: 'ExternalIntegrationCustomerSyncEndResolver',
              },
              {
                service: 'ProgressResolver',
                params: {
                  serviceIds: ['ExternalIntegrationsService'],
                } satisfies ProgressResolverParams,
              },
            ],
            resolverComponent: () => import('@/components/domain/settings/SettingsFlowDetailsPageResolver.vue'),
          },
        },
        {
          name: UserSettingsRoutes.IntegrationDetails,
          path: ':id/details',
          component: () => import('@/components/views/settings/ExternalIntegrationDetails.vue'),
          props: (route) => ({
            id: route.params.id,
          }),
          meta: {
            titler: {
              service: 'ExternalIntegrationTitlerService',
              params: {
                idKey: 'id',
                pageType: 'details',
              } satisfies ExternalIntegrationTitlerServiceParams<typeof UserSettingsRoutes.IntegrationDetails>,
            },
            resolvers: [
              {
                service: 'ExternalIntegrationStatusResolver',
              },
              {
                service: 'ExternalIntegrationCustomerSyncEndResolver',
              },
              {
                service: 'ProgressResolver',
                params: {
                  serviceIds: ['ExternalIntegrationsService'],
                } satisfies ProgressResolverParams,
              },
              {
                service: 'RedirectableSingleModelResolverService',
                params: {
                  model: ExternalIntegration,
                  source: 'router',
                  requestParam: 'id',
                  redirectToIfNotFound: { name: UserSettingsRoutes.Integrations },
                  redirectToIfAccessError: { name: UserSettingsRoutes.Integrations },
                } satisfies RedirectableSingleResolverParams,
              },
            ],
            resolverComponent: () => import('@/components/domain/settings/SettingsDetailsWithBlocksPageResolver.vue'),
          },
        },
        {
          name: UserSettingsRoutes.IntegrationSyncDetails,
          path: ':id/sync-details',
          component: () => import('@/components/views/settings/ExternalIntegrationSyncDetailsSettings.vue'),
          props: (route) => ({
            id: route.params.id,
          }),
          meta: {
            titler: {
              service: 'ExternalIntegrationTitlerService',
              params: {
                idKey: 'id',
                pageType: 'syncDetails',
              } satisfies ExternalIntegrationTitlerServiceParams<UserSettingsRoutes.IntegrationSyncDetails>,
            },
            resolvers: [
              {
                service: 'RedirectableSingleModelResolverService',
                params: {
                  model: ExternalIntegration,
                  source: 'router',
                  requestParam: 'id',
                  redirectToIfNotFound: { name: UserSettingsRoutes.Integrations },
                  redirectToIfAccessError: { name: UserSettingsRoutes.Integrations },
                } satisfies RedirectableSingleResolverParams,
              },
            ],
          },
        },
        {
          name: UserSettingsRoutes.IntegrationsMcpDetails,
          path: `mcp/:${CONNECTED_APP_ID_PARAM_NAME}`,
          component: () => import('@/components/views/settings/SettingsAccountConnectedAppsDetails.vue'),
          meta: {
            titler: {
              service: 'DomainTitlerService',
              params: {
                model: ConnectedApp,
                field: 'clientName',
                idParamName: CONNECTED_APP_ID_PARAM_NAME,
              } as DomainTitlerParams,
            },
            modules: [AppModule.Consent],
            resolvers: [
              {
                service: 'RedirectableSingleModelResolverService',
                params: {
                  model: ConnectedApp,
                  source: 'router',
                  requestParam: CONNECTED_APP_ID_PARAM_NAME,
                  redirectToIfNotFound: { name: UserSettingsRoutes.IntegrationsMcp },
                  redirectToIfAccessError: { name: UserSettingsRoutes.IntegrationsMcp },
                } satisfies RedirectableSingleResolverParams,
              },
            ],
            resolverComponent: () => import('@/components/domain/settings/SettingsDetailsWithBlocksPageResolver.vue'),
          },
        },
      ],
    },
    {
      name: UserSettingsRoutes.EmailToSmsIndex,
      path: 'email-to-sms',
      meta: {
        permission: 'emailToSms',
        modules: [AppModule.EmailToSms],
      },
      redirect: { name: UserSettingsRoutes.EmailToSmsTabsIndex },
      children: [
        {
          name: UserSettingsRoutes.EmailToSmsTabsIndex,
          path: '',
          meta: {
            permission: 'emailToSms',
            modules: [AppModule.EmailToSms],
          },
          redirect: { name: UserSettingsRoutes.EmailToSmsAllowedEmails },
          component: () => import('@/components/views/settings/SettingsEmailToSms.vue'),
          children: [
            {
              name: UserSettingsRoutes.EmailToSmsAllowedEmails,
              path: '',
              component: () => import('@/components/views/services/emailToSms/AllowedEmails.vue'),
              meta: {
                tabTitle: 'pageTitle.user.settings.emailToSmsAllowedEmails',
              },
            },
            {
              name: UserSettingsRoutes.EmailToSmsSendReceiveSettings,
              path: 'send-and-receive-settings',
              meta: {
                tabTitle: 'pageTitle.user.settings.emailToSmsSendReceiveSettings',
                resolvers: [
                  {
                    service: 'SendReceiveSettingsResolver',
                  },
                ],
              },
              component: () => import('@/components/views/services/emailToSms/SendReceiveSettings.vue'),
            },
            {
              name: UserSettingsRoutes.EmailToSmsDistributionLists,
              path: 'distribution-lists',
              component: () => import('@/components/views/services/emailToSms/Distribution.vue'),
              meta: {
                tabTitle: 'pageTitle.user.settings.emailToSmsDistributionLists',
                permission: 'distributionLists',
              },
            },
          ],
        },
        {
          name: UserSettingsRoutes.EmailToSmsCreateDistribution,
          path: 'distribution-lists/new',
          component: () => import('@/components/views/settings/SettingsEmailToSmsDistributionListFlow.vue'),
          props: () => {
            return {} satisfies ComponentProps<typeof SettingsEmailToSmsDistributionListFlow>
          },
          meta: {
            tabTitle: 'pageTitle.user.settings.distributionListsCreate',
            permission: 'distributionLists',
            modules: [
              AppModule.RecentRecipient,
              AppModule.Contacts,
              AppModule.EmailToSms,
              AppModule.ContactCounter,
              AppModule.MessageSend,
              AppModule.TenDlcTollFree, // TollFreePhoneNumbersService via composeSenderRegistrationAlerts → ServiceDistributionAdditionalSettings
            ],
            resolvers: [
              {
                service: 'UserNumbersResolverService',
              },
              {
                service: 'SenderSettingsCountriesResolverService',
              },
            ],
          },
        },
        {
          name: UserSettingsRoutes.EmailToSmsEditDistribution,
          path: 'distribution-lists/:distributionId',
          component: () => import('@/components/views/settings/SettingsEmailToSmsDistributionListFlow.vue'),
          props: (route) => {
            return {
              distributionId: route.params.distributionId,
            } satisfies ComponentProps<typeof SettingsEmailToSmsDistributionListFlow>
          },
          meta: {
            tabTitle: 'pageTitle.user.settings.distributionListsEdit',
            permission: 'distributionLists',
            resolvers: [
              {
                service: 'RedirectableSingleModelResolverService',
                params: {
                  source: 'router',
                  requestParam: 'distributionId',
                  model: DistributionDetails,
                  redirectToIfNotFound: { name: UserSettingsRoutes.EmailToSmsDistributionLists },
                  redirectToIfAccessError: { name: UserSettingsRoutes.EmailToSmsDistributionLists },
                } as NotFoundResolverParams,
              },
              {
                service: 'UserNumbersResolverService',
              },
              {
                service: 'SenderSettingsCountriesResolverService',
              },
            ],
            resolverComponent: () => import('@/components/domain/settings/SettingsFlowDetailsPageResolver.vue'),
            modules: [
              AppModule.RecentRecipient,
              AppModule.Contacts,
              AppModule.EmailToSms,
              AppModule.ContactCounter,
              AppModule.FilteredViews,
              AppModule.Segments,
              AppModule.MessageSend,
              AppModule.TenDlcTollFree, // TollFreePhoneNumbersService via composeSenderRegistrationAlerts → ServiceDistributionAdditionalSettings
            ],
          },
        },
      ],
    },
    {
      name: UserSettingsRoutes.AutoRespondersIndex,
      path: 'auto-responders',
      meta: {
        permission: 'automationRules',
        accessControlGroups: ['AutoRespondersStrategy'],
        modules: [AppModule.AutomationRule, AppModule.AutomationRules, AppModule.AutomationRulesLog],
      },
      redirect: { name: UserSettingsRoutes.AutoRespondersTabsIndex },
      children: [
        {
          name: UserSettingsRoutes.AutoRespondersTabsIndex,
          path: '',
          meta: {
            permission: 'automationRules',
            modules: [AppModule.AutomationRule, AppModule.AutomationRules, AppModule.AutomationRulesLog],
          },
          component: () => import('@/components/views/settings/SettingsAutomationRules.vue'),
          redirect: { name: UserSettingsRoutes.AutoResponders },
          children: [
            {
              name: UserSettingsRoutes.AutoResponders,
              path: '',
              component: () => import('@/components/views/services/automationRules/AutomationRulesListView.vue'),
              meta: {
                tabTitle: 'pageTitle.user.settings.autoResponders',
              },
            },
            {
              name: UserSettingsRoutes.AutoRespondersKeywords,
              path: 'keywords',
              component: () => import('@/components/views/services/automationRules/AutomationRulesKeywords.vue'),
              meta: {
                tabTitle: 'pageTitle.user.settings.autoRespondersKeywords',
                modules: [AppModule.Keywords],
              },
            },
            {
              name: UserSettingsRoutes.AutoRespondersLogs,
              path: 'logs',
              meta: {
                tabTitle: 'pageTitle.user.settings.autoRespondersLogs',
              },
              component: () => import('@/components/views/services/automationRules/AutomationRulesLogs.vue'),
            },
          ],
        },
        {
          name: UserSettingsRoutes.AutoRespondersNewRule,
          path: 'new',
          props: () => {
            return {} satisfies ComponentProps<typeof SettingsAutomationRulesFlow>
          },
          meta: {
            modules: getAutomationRuleDetails(),
            tabTitle: 'pageTitle.user.settings.autoRespondersNewRule',
          },
          component: () => import('@/components/views/settings/SettingsAutomationRulesFlow.vue'),
        },
        {
          name: UserSettingsRoutes.AutoRespondersEditRule,
          path: ':ruleId',
          component: () => import('@/components/views/settings/SettingsAutomationRulesFlow.vue'),
          props: (route) => {
            return {
              ruleId: route.params.ruleId,
            } satisfies ComponentProps<typeof SettingsAutomationRulesFlow>
          },
          meta: {
            modules: getAutomationRuleDetails(),
            tabTitle: 'pageTitle.user.settings.autoRespondersDetails',
            resolvers: [
              {
                service: 'RedirectableSingleModelResolverService',
                params: {
                  source: 'router',
                  requestParam: 'ruleId',
                  model: AutomationRuleDetails,
                  redirectToIfNotFound: { name: UserSettingsRoutes.AutoRespondersIndex },
                } as NotFoundResolverParams,
              },
            ],
          },
        },
        {
          name: UserSettingsRoutes.AutoRespondersLogsDetails,
          path: 'logs/:automationRuleLogId',
          props: (route) => {
            return {
              automationRuleLogId: route.params.automationRuleLogId,
            } satisfies ComponentProps<typeof SettingsAutomationRuleLogDetails>
          },
          meta: {
            tabTitle: 'pageTitle.user.settings.autoRespondersLogDetails',
            detailsPage: {
              source: 'router',
              requestParam: 'automationRuleLogId',
              model: AutomationRuleLog,
            } as SingleModelResolverParams,
            modules: getAutomationRuleDetails(),
            resolverComponent: () => import('@/components/domain/settings/SettingsDetailsPageResolver.vue'),
          },
          component: () => import('@/components/views/settings/SettingsAutomationRuleLogDetails.vue'),
        },
        {
          name: UserSettingsRoutes.AutoRespondersDetails,
          path: 'details/:ruleId',
          props: true,
          meta: {
            tabTitle: 'pageTitle.user.settings.autoRespondersDetails',
            detailsPage: {
              source: 'router',
              requestParam: 'ruleId',
              model: AutomationRuleDetails,
            } as SingleModelResolverParams,
            redirectToIfNotFound: { name: UserSettingsRoutes.AutoRespondersIndex },
            resolverComponent: () => import('@/components/domain/settings/SettingsDetailsPageResolver.vue'),
            modules: getAutomationRuleDetails(),
          },
          component: () => import('@/components/views/settings/SettingsAutomationRuleDetails.vue'),
        },
      ],
    },
    {
      name: UserSettingsRoutes.TicketsIndex,
      path: 'tickets',
      meta: {
        resolvers: [
          {
            service: 'SettingsTicketsResolverService',
          },
        ],
      },
      redirect: { name: UserSettingsRoutes.Tickets },
      children: [
        {
          name: UserSettingsRoutes.Tickets,
          path: '',
          component: () => import('@/components/views/settings/SettingsTickets.vue'),
          meta: {
            tabTitle: 'pageTabTitle.tickets.settings.index',
          },
        },
        {
          name: UserSettingsRoutes.TicketsInboxesIndex,
          path: 'inboxes',
          redirect: { name: UserSettingsRoutes.TicketsInboxes },
          children: [
            {
              name: UserSettingsRoutes.TicketsInboxes,
              path: '',
              component: () => import('@/components/views/settings/SettingsTicketsConnectedInboxes.vue'),
              meta: {
                tabTitle: 'pageTitle.user.settings.ticketsConnectedInboxes',
                resolvers: [
                  {
                    service: 'TicketInboxForwardingVerificationEventsResolverService',
                  },
                ],
              },
            },
            {
              name: UserSettingsRoutes.TicketsInboxesConnect,
              path: 'connect',
              meta: {
                tabTitle: 'pageTabTitle.tickets.settings.inboxes.connect',
                permission: 'connectInboxes',
                resolvers: [
                  {
                    service: 'SingleModelResolverService',
                    params: { model: TicketWorkspace, id: TICKET_WORKSPACE_ID },
                  },
                  {
                    service: 'TicketConnectInboxContinueResolverService',
                  },
                  {
                    service: 'TicketInboxForwardingVerificationEventsResolverService',
                  },
                ],
                resolverComponent: () => import('@/components/domain/settings/SettingsFlowDetailsPageResolver.vue'),
              },
              component: () => import('@/components/views/settings/SettingsTicketsConnectNewInbox.vue'),
            },
            {
              name: UserSettingsRoutes.TicketsInboxesDetails,
              path: ':inboxId',
              component: () => import('@/components/views/settings/SettingsTicketsConnectedInboxDetails.vue'),
              meta: {
                tabTitle: 'pageTabTitle.tickets.settings.inboxes.details',
                resolvers: [
                  {
                    service: 'SingleModelResolverService',
                    params: {
                      model: TicketInbox,
                      source: 'router',
                      requestParam: 'inboxId',
                    } satisfies SingleModelResolverParams,
                  },
                  {
                    service: 'SingleModelResolverService',
                    params: { model: TicketWorkspace, id: TICKET_WORKSPACE_ID },
                  },
                ],
                resolverComponent: () =>
                  import('@/components/domain/settings/SettingsDetailsWithBlocksPageResolver.vue'),
              },
            },
          ],
        },
        {
          name: UserSettingsRoutes.TicketsSpamFilter,
          path: 'spam-filter',
          meta: {
            tabTitle: 'pageTabTitle.tickets.settings.spamFilter',
            resolvers: [
              {
                service: 'SingleModelResolverService',
                params: { model: TicketSpamFilter, id: TICKET_SPAM_FILTER_ID, cache: false },
              },
            ],
            resolverComponent: () => import('@/components/domain/settings/SettingsDetailsWithBlocksPageResolver.vue'),
          },
          component: () => import('@/components/views/settings/SettingsTicketsSpamFilter.vue'),
        },
        {
          name: UserSettingsRoutes.TicketsGeneral,
          path: 'general',
          meta: {
            tabTitle: 'pageTabTitle.tickets.settings.general',
            resolvers: [
              {
                service: 'SingleModelResolverService',
                params: { model: TicketWorkspace, id: TICKET_WORKSPACE_ID },
              },
            ],
            resolverComponent: () => import('@/components/domain/settings/SettingsDetailsWithBlocksPageResolver.vue'),
          },
          component: () => import('@/components/views/settings/SettingsTicketsGeneral.vue'),
        },
        {
          name: UserSettingsRoutes.TicketsFilteredViewsIndex,
          path: 'filtered-views',
          redirect: { name: UserSettingsRoutes.TicketsFilteredViews },
          children: [
            {
              name: UserSettingsRoutes.TicketsFilteredViews,
              path: '',
              meta: {
                tabTitle: 'pageTabTitle.tickets.settings.filteredViews',
                resolvers: [
                  {
                    service: 'SingleModelResolverService',
                    params: { model: TicketWorkspace, id: TICKET_WORKSPACE_ID },
                  },
                ],
              },
              component: () => import('@/components/views/settings/SettingsTicketsFilteredViews.vue'),
            },
            {
              name: UserSettingsRoutes.TicketsFilteredViewsCreate,
              path: 'new',
              meta: {
                tabTitle: 'pageTabTitle.tickets.settings.filteredViewsCreate',
                resolvers: [
                  {
                    service: 'ModelResolver',
                    params: [TicketStatus],
                  },
                  {
                    service: 'TicketsBasicDataResolverService',
                  },
                ],
                resolverComponent: () =>
                  import('@/components/domain/settings/SettingsDetailsWithBlocksPageResolver.vue'),
              },
              component: () => import('@/components/views/settings/SettingsTicketsFilteredViewsCreate.vue'),
            },
            {
              name: UserSettingsRoutes.TicketsFilteredViewsDetails,
              path: `edit/:${FILTERED_VIEW_PARAM_NAME}`,
              meta: {
                tabTitle: 'pageTabTitle.tickets.settings.filteredViewsDetails',
                resolvers: [
                  {
                    service: 'SingleModelResolverService',
                    params: { model: TicketWorkspace, id: FILTERED_VIEW_PARAM_NAME },
                  },
                  {
                    service: 'ModelResolver',
                    params: [TicketStatus],
                  },
                  {
                    service: 'TicketsBasicDataResolverService',
                  },
                  [
                    {
                      service: 'FilteredViewsResolverService',
                      params: [FilteredViewsSupportedEntities.TICKETS],
                    },
                    {
                      service: 'EditableFilteredViewsResolverService',
                      params: {
                        idParam: FILTERED_VIEW_PARAM_NAME,
                        entity: FilteredViewsSupportedEntities.TICKETS,
                        redirectToIfNotFound: {
                          name: UserSettingsRoutes.TicketsFilteredViews,
                        },
                      } satisfies EditableFilteredViewsResolverServiceParams,
                    },
                  ],
                ],
                resolverComponent: () =>
                  import('@/components/domain/settings/SettingsDetailsWithBlocksPageResolver.vue'),
              },
              props: (route) => {
                return {
                  fvId: route.params[FILTERED_VIEW_PARAM_NAME],
                } satisfies ComponentProps<typeof SettingsTicketsFilteredViewsDetails>
              },
              component: () => import('@/components/views/settings/SettingsTicketsFilteredViewsDetails.vue'),
            },
          ],
        },
      ],
    },

    {
      name: UserSettingsRoutes.Tasks,
      path: 'tasks',
      meta: {
        tabTitle: 'pageTitle.user.tasks.settings',
        accessControlGroups: ['TasksReadOnlyStrategy'],
        modules: [AppModule.TasksBase, AppModule.Tasks],
        resolvers: [{ service: 'TaskSettingsResolver' }, { service: 'FetchAllAccountsResolver' }],
        resolverComponent: () => import('@/components/domain/settings/SettingsDetailsWithBlocksPageResolver.vue'),
        resolverComponentProps: { hideBreadcrumbsSkeleton: true },
      },
      redirect: { name: UserSettingsRoutes.TasksOverview },
      children: [
        {
          name: UserSettingsRoutes.TasksOverview,
          path: '',
          component: () => import('@/components/views/settings/SettingsTasks.vue'),
        },
        {
          name: UserSettingsRoutes.TasksBoardRoot,
          path: ':boardId',
          props: true,
          redirect: { name: UserSettingsRoutes.TasksBoard },
          children: [
            {
              name: UserSettingsRoutes.TasksBoard,
              path: '',
              props: true,
              meta: {
                resolvers: [
                  {
                    service: 'RedirectableSingleModelResolverService',
                    params: {
                      requestParam: 'boardId',
                      model: TaskBoard,
                      source: 'router',
                      redirectToIfNotFound: { name: UserSettingsRoutes.TasksOverview },
                    } as NotFoundResolverParams,
                  },
                ],
                resolverComponent: () =>
                  import('@/components/domain/settings/SettingsDetailsWithBlocksPageResolver.vue'),
              },
              component: () => import('@/components/views/settings/SettingsTasksBoard.vue'),
            },
            {
              name: UserSettingsRoutes.TasksBoardArchived,
              path: 'archived',
              props: true,
              meta: {
                resolvers: [
                  {
                    service: 'RedirectableSingleModelResolverService',
                    params: {
                      requestParam: 'boardId',
                      model: TaskBoardArchived,
                      source: 'router',
                      redirectToIfNotFound: { name: UserSettingsRoutes.TasksOverview },
                    } as NotFoundResolverParams,
                  },
                ],
                resolverComponent: () =>
                  import('@/components/domain/settings/SettingsDetailsWithBlocksPageResolver.vue'),
              },
              component: () => import('@/components/views/settings/SettingsTasksBoard.vue'),
            },
            {
              name: UserSettingsRoutes.TasksBoardStage,
              props: true,
              path: 'stages',
              meta: {
                resolvers: [
                  {
                    service: 'RedirectableSingleModelResolverService',
                    params: {
                      requestParam: 'boardId',
                      model: TaskBoard,
                      source: 'router',
                      redirectToIfNotFound: { name: UserSettingsRoutes.TasksOverview },
                    } as NotFoundResolverParams,
                  },
                ],
                resolverComponent: () =>
                  import('@/components/domain/settings/SettingsDetailsWithBlocksPageResolver.vue'),
              },
              component: () => import('@/components/views/settings/SettingsTasksBoardStage.vue'),
            },
          ],
        },
      ],
    },
    {
      name: UserSettingsRoutes.Deals,
      path: 'deals',
      meta: {
        tabTitle: 'pageTitle.user.deals.settings',
        accessControlGroups: ['DealsReadOnlyStrategy'],
        modules: [AppModule.Deals],
        resolvers: [{ service: 'DealPipelineSettingsResolverService' }],
        resolverComponent: () => import('@/components/domain/settings/SettingsDetailsWithBlocksPageResolver.vue'),
        resolverComponentProps: { hideBreadcrumbsSkeleton: true },
      },
      redirect: { name: UserSettingsRoutes.DealsOverview },
      children: [
        {
          name: UserSettingsRoutes.DealsOverview,
          path: '',
          component: () => import('@/components/views/settings/SettingsDeals.vue'),
        },
        {
          name: UserSettingsRoutes.DealsPipelineRoot,
          path: ':pipelineId',
          props: true,
          redirect: { name: UserSettingsRoutes.DealsPipeline },
          children: [
            {
              name: UserSettingsRoutes.DealsPipeline,
              path: '',
              props: true,
              meta: {
                resolvers: [
                  {
                    service: 'RedirectableSingleModelResolverService',
                    params: {
                      requestParam: 'pipelineId',
                      model: DealPipeline,
                      source: 'router',
                      redirectToIfNotFound: { name: UserSettingsRoutes.DealsOverview },
                    } as NotFoundResolverParams,
                  },
                ],
                resolverComponent: () =>
                  import('@/components/domain/settings/SettingsDetailsWithBlocksPageResolver.vue'),
              },
              component: () => import('@/components/views/settings/SettingsDealsPipeline.vue'),
            },
            {
              name: UserSettingsRoutes.DealsPipelineArchived,
              path: 'archived',
              props: true,
              meta: {
                resolvers: [
                  {
                    service: 'RedirectableSingleModelResolverService',
                    params: {
                      requestParam: 'pipelineId',
                      model: DealPipelineArchived,
                      source: 'router',
                      redirectToIfNotFound: { name: UserSettingsRoutes.DealsOverview },
                    } as NotFoundResolverParams,
                  },
                ],
                resolverComponent: () =>
                  import('@/components/domain/settings/SettingsDetailsWithBlocksPageResolver.vue'),
              },
              component: () => import('@/components/views/settings/SettingsDealsPipeline.vue'),
            },
            {
              name: UserSettingsRoutes.DealsPipelineStage,
              props: true,
              path: 'stages',
              meta: {
                resolvers: [
                  {
                    service: 'RedirectableSingleModelResolverService',
                    params: {
                      requestParam: 'pipelineId',
                      model: DealPipeline,
                      source: 'router',
                      redirectToIfNotFound: { name: UserSettingsRoutes.DealsOverview },
                    } as NotFoundResolverParams,
                  },
                ],
                resolverComponent: () =>
                  import('@/components/domain/settings/SettingsDetailsWithBlocksPageResolver.vue'),
              },
              component: () => import('@/components/views/settings/SettingsDealsPipelineStage.vue'),
            },
          ],
        },
      ],
    },
    {
      name: UserSettingsRoutes.PointAiIndex,
      path: 'point-ai',
      component: PassThrough,
      meta: {
        modules: [AppModule.PointAi],
        tabTitle: 'pageTitle.user.services.pointAi',
        permission: 'pointAi',
        resolvers: [{ service: 'PointAiChatResolver' }],
        accessControlGroups: ['PointAiManagementStrategy'],
      },
      redirect: { name: UserSettingsRoutes.PointAi },
      children: [
        {
          name: UserSettingsRoutes.PointAi,
          path: '',
          component: () => import('@/components/views/settings/SettingsPointAi.vue'),
          redirect: { name: UserSettingsRoutes.PointAiWebsites },
          children: [
            {
              name: UserSettingsRoutes.PointAiWebsites,
              path: 'websites',
              component: () => import('@/components/views/services/pointAi/PointAiWebsites.vue'),
            },
            {
              name: UserSettingsRoutes.PointAiDocuments,
              path: 'documents',
              component: () => import('@/components/views/services/pointAi/PointAiDocuments.vue'),
            },
          ],
        },
        {
          name: UserSettingsRoutes.PointAiWebsitesDownloaded,
          path: 'websites/:id',
          props: true,
          component: () => import('@/components/views/settings/SettingsPointAiWebsitesDownloaded.vue'),
        },
      ],
    },
    {
      name: UserSettingsRoutes.WorkspaceIndex,
      path: 'workspace',
      redirect: { name: UserSettingsRoutes.WorkspaceSubaccounts },
      component: () => import('@/components/views/settings/SettingsWorkspace.vue'),
      children: [
        {
          name: UserSettingsRoutes.WorkspaceSubaccounts,
          path: 'subaccounts',
          meta: {
            permission: 'subaccountSettings',
            accessControlGroups: ['SettingsWorkspaceSubaccountsStrategy'],
            modules: [
              AppModule.AccountSettings,
              AppModule.SubAccounts,
              AppModule.SubAccountsForm,
              AppModule.TeamWorkflow,
            ],
            tabTitle: 'pageTabTitle.user.settings.subAccounts',
          },
          component: () => import('@/components/views/accounts/subAccounts/SubAccountsAll.vue'),
        },
        {
          name: UserSettingsRoutes.WorkspaceUsers,
          path: 'users',
          meta: {
            permission: 'subaccountSettings',
            accessControlGroups: ['SettingsWorkspaceUsersStrategy'],
            modules: [
              AppModule.AccountSettings,
              AppModule.SubAccounts,
              AppModule.SubAccountsForm,
              AppModule.TeamWorkflow,
            ],
            tabTitle: 'pageTabTitle.user.settings.users',
          },
          component: () => import('@/components/views/settings/SettingsWorkspaceUsers.vue'),
        },
        {
          name: UserSettingsRoutes.WorkspaceSso,
          path: 'sso',
          component: () => import('@/components/views/settings/SettingsAccountSso.vue'),
          props: { tab: true },
          meta: {
            accessControlGroups: ['UsersWorkflowAdminRoleStrategy'],
            modules: [AppModule.AccountSettings],
            resolvers: [
              {
                service: 'ModelSettingsResolverService',
                params: {
                  model: AccountSsoSettings,
                  redirect: { name: 'user.accounts.settings' },
                },
              },
            ],
            tabTitle: 'pageTitle.user.accounts.sso',
            permission: 'accountSettings',
          },
        },
        {
          name: UserSettingsRoutes.WorkspaceDataRetention,
          path: 'data-retention',
          component: () => import('@/components/views/settings/SettingsAccountDataRetention.vue'),
          meta: {
            tabTitle: 'pageTitle.user.accounts.dataRetention',
            resolvers: [
              {
                service: 'MyDataCountersResolver',
              },
            ],
            permission: 'messagingData',
            modules: [AppModule.MyData],
            accessControlGroups: ['UsersWorkflowNotUserRoleStrategy'],
          },
        },
      ],
    },
    {
      name: UserSettingsRoutes.WorkspaceSubaccountsImport,
      path: 'workspace/subaccounts/import/flow',
      component: () => import('@/components/views/settings/SettingsWorkspaceSubaccountsImport.vue'),
      meta: {
        permission: 'subaccountSettings',
        modules: [AppModule.SubAccountsImportDetails, AppModule.SubAccountsImportFlow],
        accessControlGroups: ['SubAccountsImportFlowStrategy'],
        tabTitle: 'pageTabTitle.user.accounts.subAccounts.importFlow',
        resolvers: [
          {
            service: 'ProgressResolver',
            params: {
              serviceIds: ['SubAccountImportSessionService'],
            } satisfies ProgressResolverParams,
          },
        ],
      },
    },
    {
      name: UserSettingsRoutes.WorkspaceSubaccountsImportDetailsIndex,
      path: 'workspace/subaccounts/import/:subAccountImportId',
      component: () => import('@/components/views/settings/SettingsWorkspaceSubaccountsImportDetails.vue'),
      props: (route) => {
        return {
          subAccountImportId: route.params.subAccountImportId,
        } satisfies ComponentProps<typeof SettingsWorkspaceSubaccountsImportDetails>
      },
      redirect: { name: UserSettingsRoutes.WorkspaceSubaccountsImportDetailsInvited },
      meta: {
        tabTitle: 'pageTabTitle.user.accounts.subAccounts.importDetails',
        resolvers: [
          { service: 'SubAccountsImportResolverService' },
          {
            service: 'FacetResolver',
            params: {
              model: SubAccountsImportDetails,
              source: 'router',
              requestParam: 'subAccountImportId',
              subscriptionModels: [SubAccountsImportDetails],
            },
          },
        ],
        permission: 'subaccountSettings',
        modules: [AppModule.SubAccountsImportDetails],
      },
      children: [
        {
          name: UserSettingsRoutes.WorkspaceSubaccountsImportDetailsInvited,
          path: 'invited',
          component: () =>
            import('@/components/views/accounts/subAccounts/import/details/SubAccountsImportDetailsTableWrapper.vue'),
          meta: { type: 'invited' },
        },
        {
          name: UserSettingsRoutes.WorkspaceSubaccountsImportDetailsDuplicated,
          path: 'duplicated',
          component: () =>
            import('@/components/views/accounts/subAccounts/import/details/SubAccountsImportDetailsTableWrapper.vue'),
          meta: { type: 'duplicated' },
        },
        {
          name: UserSettingsRoutes.WorkspaceSubaccountsImportDetailsFailed,
          path: 'failed',
          component: () =>
            import('@/components/views/accounts/subAccounts/import/details/SubAccountsImportDetailsTableWrapper.vue'),
          meta: { type: 'failed' },
        },
      ],
    },
    getSubAccountsDetailsRoute(),
    getUsersDetailsRoute(),
    {
      name: UserSettingsRoutes.BillingIndex,
      path: 'billing',
      component: () => import('@/components/views/settings/SettingsBilling.vue'),
      redirect: { name: UserSettingsRoutes.BillingOverview },
      children: [
        {
          name: UserSettingsRoutes.BillingOverview,
          path: '',
          component: () => import('@/components/views/settings/SettingsBillingOverview.vue'),
          meta: {
            permission: 'autorechargeSettings',
            tabTitle: 'pageTitle.user.settings.billingOverview',
            modules: [AppModule.Billing, AppModule.ByocHelper, AppModule.StripeBilling],
            resolvers: [
              {
                service: 'VerifyBillingAllowedResolver',
              },
            ],
          },
        },
        {
          name: UserSettingsRoutes.BillingPlans,
          path: 'monthly-plans',
          component: () => import('@/components/views/settings/SettingsBillingPlansWrapper.vue'),
          meta: {
            tabTitle: 'pageTitle.user.settings.billingPlans',
            modules: [AppModule.Billing, AppModule.StripeBilling],
            accessControlGroups: ['MonthlyPlansStrategy'],
          },
        },
        {
          name: UserSettingsRoutes.BillingInvoices,
          path: 'invoices',
          component: () => import('@/components/views/accounts/InvoicesPayAsYouGo.vue'),
          meta: {
            permission: 'invoices',
            tabTitle: 'pageTitle.user.settings.billingInvoices',
            modules: [AppModule.InvoicesBase, AppModule.Invoices],
          },
        },
        {
          name: UserSettingsRoutes.BillingStatements,
          path: 'statements',
          component: () => import('@/components/views/accounts/StatementsTable.vue'),
          meta: {
            permission: 'reportingStatements',
            tabTitle: 'pageTitle.user.settings.billingStatements',
            modules: [AppModule.Statements],
            accessControlGroups: ['UsersWorkflowNotUserRoleStrategy'],
          },
        },
      ],
    },
    {
      name: UserSettingsRoutes.AccountIndex,
      path: 'account',
      meta: {
        modules: [AppModule.AccountSettings],
      },
      redirect: { name: UserSettingsRoutes.AccountTabsIndex },
      children: [
        {
          name: UserSettingsRoutes.AccountTabsIndex,
          path: '',
          meta: {
            modules: [AppModule.AccountSettings],
          },
          redirect: { name: UserSettingsRoutes.AccountDetails },
          component: () => import('@/components/views/settings/SettingsAccount.vue'),
          children: [
            {
              name: UserSettingsRoutes.AccountDetails,
              path: '',
              component: () => import('@/components/views/settings/SettingsAccountDetails.vue'),
              meta: {
                permission: 'accountSettings',
                tabTitle: 'pageTitle.user.settings.accountDetails',
              },
            },
            {
              name: UserSettingsRoutes.AccountSecurity,
              path: 'security',
              component: () => import('@/components/views/settings/SettingsAccountSecurity.vue'),
              meta: {
                permission: 'accountSettings',
                tabTitle: 'pageTitle.user.settings.accountSecurity',
              },
            },
            {
              name: UserSettingsRoutes.AccountNotifications,
              path: 'notifications',
              component: () => import('@/components/views/settings/SettingsAccountNotifications.vue'),
              meta: {
                permission: 'accountNotifications',
                modules: [AppModule.Notifications],
                tabTitle: 'pageTitle.user.settings.accountNotifications',
              },
            },
          ],
        },
        {
          name: UserSettingsRoutes.AccountSecurity2faSetup,
          path: 'security/two-factor-authentication',
          component: () => import('@/components/views/settings/SettingsAccountSecurity2faSetup.vue'),
          meta: {
            tabTitle: 'pageTitle.user.settings.accountSecurity2faSetup',
            parent: UserSettingsRoutes.AccountSecurity,
            permission: 'accountSettings',
            modules: [AppModule.TFAPages],
            accessControlGroups: ['TwoFactorSetupStrategy'],
            resolvers: [
              {
                service: 'ModelResolver',
                params: [AccountSettingsSecurity],
              },
            ],
            resolverComponent: () =>
              import('@/components/shared/templates/settingsPage/resolvers/SettingsPageCommonResolver.vue'),
          },
        },
      ],
    },
  ],
}
